/**
 * Coordinate and location labels
 *
 * Sexagesimal values are rounded once, to tenths of a second, before being
 * split into fields, so 59.97" carries into the next minute instead of
 * showing as 60.0", and the sign is taken from the value itself so
 * declinations between 0° and -1° keep their minus sign.
 *
 * @package Astrophotography Planning Calculator
 */

/**
 * Split a non-negative value into [whole, minutes, tenths of seconds]
 * @param {number} value - Hours or degrees
 * @returns {number[]}
 */
function splitSexagesimal(value) {
    const tenths = Math.round(value * 36000);
    return [Math.floor(tenths / 36000), Math.floor((tenths % 36000) / 600), (tenths % 600) / 10];
}

/**
 * @param {number} hours - Right ascension in hours
 * @returns {string} e.g. "0h 42m 44.3s"
 */
export function formatRA(hours) {
    const [h, m, s] = splitSexagesimal(((hours % 24) + 24) % 24);
    return `${h % 24}h ${m}m ${s.toFixed(1)}s`;
}

/**
 * @param {number} degrees - Declination in degrees
 * @returns {string} e.g. "-5° 23' 15.0\""
 */
export function formatDec(degrees) {
    const [d, m, s] = splitSexagesimal(Math.abs(degrees));
    return `${degrees < 0 ? '-' : '+'}${d}° ${m}' ${s.toFixed(1)}"`;
}

/**
 * @param {number} latitude - Degrees, negative south
 * @param {number} [digits] - Decimal places; the number as given when omitted
 * @returns {string} e.g. "35.0°S"
 */
export function formatLatitude(latitude, digits) {
    const value = Math.abs(latitude);
    return `${digits === undefined ? value : value.toFixed(digits)}°${latitude < 0 ? 'S' : 'N'}`;
}

/**
 * @param {number} longitude - Degrees, negative west
 * @param {number} [digits] - Decimal places; the number as given when omitted
 * @returns {string} e.g. "6.0°W"
 */
export function formatLongitude(longitude, digits) {
    const value = Math.abs(longitude);
    return `${digits === undefined ? value : value.toFixed(digits)}°${longitude < 0 ? 'W' : 'E'}`;
}
